<template>
	<div class="merchant-categories-container">
		<ModalHeader 
			:title="getString('merchant_categories')" 
			:button-text="getString('apply')" 
			@click-button="apply" 
		/>
		<div class="categories-search">
			<AppTextInput
				iconName="icon-magnify"
				:placeholder="getString('search_categories')" 
				:value="searchQuery" 
				@changed-text="(value) => searchQuery = value"
			/>
		</div>
		<div class="categories-list">
			<AppList :items="filteredCategories" />
		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue';
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import { useLanguageStore } from "@/shared/stores";

// Merchant categories from Stripe documentation
const MERCHANT_CATEGORIES = [
	{ code: 'ac_refrigeration_repair', name: 'A/C, Refrigeration Repair' },
	{ code: 'accounting_bookkeeping_services', name: 'Accounting/Bookkeeping Services' },
	{ code: 'advertising_services', name: 'Advertising Services' },
	{ code: 'agricultural_cooperative', name: 'Agricultural Cooperative' },
	{ code: 'airlines_air_carriers', name: 'Airlines, Air Carriers' },
	{ code: 'airports_flying_fields', name: 'Airports, Flying Fields' },
	{ code: 'ambulance_services', name: 'Ambulance Services' },
	{ code: 'amusement_parks_carnivals', name: 'Amusement Parks/Carnivals' },
	{ code: 'antique_reproductions', name: 'Antique Reproductions' },
	{ code: 'antique_shops', name: 'Antique Shops' },
	{ code: 'aquariums', name: 'Aquariums' },
	{ code: 'architectural_surveying_services', name: 'Architectural/Surveying Services' },
	{ code: 'art_dealers_and_galleries', name: 'Art Dealers and Galleries' },
	{ code: 'artists_supply_and_craft_shops', name: 'Artists Supply and Craft Shops' },
	{ code: 'auto_body_repair_shops', name: 'Auto Body Repair Shops' },
	{ code: 'auto_paint_shops', name: 'Auto Paint Shops' },
	{ code: 'auto_service_shops', name: 'Auto Service Shops' },
	{ code: 'auto_and_home_supply_stores', name: 'Auto and Home Supply Stores' },
	{ code: 'automated_cash_disburse', name: 'Automated Cash Disburse' },
	{ code: 'automated_fuel_dispensers', name: 'Automated Fuel Dispensers' },
	{ code: 'automobile_associations', name: 'Automobile Associations' },
	{ code: 'automotive_parts_and_accessories_stores', name: 'Automotive Parts and Accessories Stores' },
	{ code: 'automotive_tire_stores', name: 'Automotive Tire Stores' },
	{ code: 'bail_and_bond_payments', name: 'Bail and Bond Payments' },
	{ code: 'bakeries', name: 'Bakeries' },
	{ code: 'bands_orchestras', name: 'Bands, Orchestras' },
	{ code: 'barber_and_beauty_shops', name: 'Barber and Beauty Shops' },
	{ code: 'betting_casino_gambling', name: 'Betting/Casino Gambling' },
	{ code: 'bicycle_shops', name: 'Bicycle Shops' },
	{ code: 'billiard_pool_establishments', name: 'Billiard/Pool Establishments' },
	{ code: 'boat_dealers', name: 'Boat Dealers' },
	{ code: 'boat_rentals_and_leases', name: 'Boat Rentals and Leases' },
	{ code: 'book_stores', name: 'Book Stores' },
	{ code: 'books_periodicals_and_newspapers', name: 'Books, Periodicals, and Newspapers' },
	{ code: 'bowling_alleys', name: 'Bowling Alleys' },
	{ code: 'bus_lines', name: 'Bus Lines' },
	{ code: 'business_secretarial_schools', name: 'Business/Secretarial Schools' },
	{ code: 'buying_shopping_services', name: 'Buying/Shopping Services' },
	{ code: 'cable_satellite_and_other_pay_television_and_radio', name: 'Cable, Satellite, and Other Pay Television and Radio' },
	{ code: 'camera_and_photographic_supply_stores', name: 'Camera and Photographic Supply Stores' },
	{ code: 'candy_nut_and_confectionery_stores', name: 'Candy, Nut, and Confectionery Stores' },
	{ code: 'car_rental_agencies', name: 'Car Rental Agencies' },
	{ code: 'car_washes', name: 'Car Washes' },
	{ code: 'car_and_truck_dealers_new_used', name: 'Car and Truck Dealers (New & Used)' },
	{ code: 'car_and_truck_dealers_used_only', name: 'Car and Truck Dealers (Used Only)' },
	{ code: 'carpentry_services', name: 'Carpentry Services' },
	{ code: 'carpet_upholstery_cleaning', name: 'Carpet/Upholstery Cleaning' },
	{ code: 'caterers', name: 'Caterers' },
	{ code: 'charitable_and_social_service_organizations_fundraising', name: 'Charitable and Social Service Organizations' },
	{ code: 'chemicals_and_allied_products', name: 'Chemicals and Allied Products' },
	{ code: 'child_care_services', name: 'Child Care Services' },
	{ code: 'childrens_and_infants_wear_stores', name: 'Childrens and Infants Wear Stores' },
	{ code: 'chiropodists_podiatrists', name: 'Chiropodists, Podiatrists' },
	{ code: 'chiropractors', name: 'Chiropractors' },
	{ code: 'cigar_stores_and_stands', name: 'Cigar Stores and Stands' },
	{ code: 'civic_social_fraternal_associations', name: 'Civic, Social, Fraternal Associations' },
	{ code: 'cleaning_and_maintenance', name: 'Cleaning and Maintenance' },
	{ code: 'clothing_rental', name: 'Clothing Rental' },
	{ code: 'colleges_universities', name: 'Colleges, Universities' },
	{ code: 'commercial_equipment', name: 'Commercial Equipment' },
	{ code: 'commercial_footwear', name: 'Commercial Footwear' },
	{ code: 'commercial_photography_art_and_graphics', name: 'Commercial Photography, Art and Graphics' },
	{ code: 'commuter_transport_and_ferries', name: 'Commuter Transport, Ferries' },
	{ code: 'computer_network_services', name: 'Computer Network Services' },
	{ code: 'computer_programming', name: 'Computer Programming' },
	{ code: 'computer_repair', name: 'Computer Repair' },
	{ code: 'computer_software_stores', name: 'Computer Software Stores' },
	{ code: 'computers_peripherals_and_software', name: 'Computers, Peripherals, and Software' },
	{ code: 'concrete_work_services', name: 'Concrete Work Services' },
	{ code: 'construction_materials', name: 'Construction Materials' },
	{ code: 'consulting_public_relations', name: 'Consulting, Public Relations' },
	{ code: 'correspondence_schools', name: 'Correspondence Schools' },
	{ code: 'cosmetic_stores', name: 'Cosmetic Stores' },
	{ code: 'counseling_services', name: 'Counseling Services' },
	{ code: 'country_clubs', name: 'Country Clubs' },
	{ code: 'courier_services', name: 'Courier Services' },
	{ code: 'court_costs', name: 'Court Costs' },
	{ code: 'credit_reporting_agencies', name: 'Credit Reporting Agencies' },
	{ code: 'cruise_lines', name: 'Cruise Lines' },
	{ code: 'dairy_products_stores', name: 'Dairy Products Stores' },
	{ code: 'dance_hall_studios_schools', name: 'Dance Hall, Studios, Schools' },
	{ code: 'dating_escort_services', name: 'Dating/Escort Services' },
	{ code: 'dentists_orthodontists', name: 'Dentists, Orthodontists' },
	{ code: 'department_stores', name: 'Department Stores' },
	{ code: 'detective_agencies', name: 'Detective Agencies' },
	{ code: 'digital_goods_media', name: 'Digital Goods Media' },
	{ code: 'digital_goods_applications', name: 'Digital Goods Applications' },
	{ code: 'digital_goods_games', name: 'Digital Goods Games' },
	{ code: 'digital_goods_large_volume', name: 'Digital Goods Large Volume' },
	{ code: 'direct_marketing_catalog_merchant', name: 'Direct Marketing - Catalog' },
	{ code: 'direct_marketing_combination_catalog_and_retail_merchant', name: 'Direct Marketing - Combination' },
	{ code: 'direct_marketing_inbound_telemarketing', name: 'Direct Marketing - Inbound' },
	{ code: 'direct_marketing_insurance_services', name: 'Direct Marketing - Insurance' },
	{ code: 'direct_marketing_other', name: 'Direct Marketing - Other' },
	{ code: 'direct_marketing_outbound_telemarketing', name: 'Direct Marketing - Outbound' },
	{ code: 'direct_marketing_subscription', name: 'Direct Marketing - Subscription' },
	{ code: 'direct_marketing_travel', name: 'Direct Marketing - Travel' },
	{ code: 'discount_stores', name: 'Discount Stores' },
	{ code: 'doctors', name: 'Doctors' },
	{ code: 'door_to_door_sales', name: 'Door-To-Door Sales' },
	{ code: 'drapery_window_covering_and_upholstery_stores', name: 'Drapery, Window Covering Stores' },
	{ code: 'drinking_places', name: 'Drinking Places' },
	{ code: 'drug_stores_and_pharmacies', name: 'Drug Stores and Pharmacies' },
	{ code: 'drugs_drug_proprietaries_and_druggist_sundries', name: 'Drugs and Druggist Sundries' },
	{ code: 'dry_cleaners', name: 'Dry Cleaners' },
	{ code: 'durable_goods', name: 'Durable Goods' },
	{ code: 'duty_free_stores', name: 'Duty Free Stores' },
	{ code: 'eating_places_restaurants', name: 'Eating Places, Restaurants' },
	{ code: 'educational_services', name: 'Educational Services' },
	{ code: 'electric_razor_stores', name: 'Electric Razor Stores' },
	{ code: 'electric_vehicle_charging', name: 'Electric Vehicle Charging' },
	{ code: 'electrical_parts_and_equipment', name: 'Electrical Parts and Equipment' },
	{ code: 'electrical_services', name: 'Electrical Services' },
	{ code: 'electronics_repair_shops', name: 'Electronics Repair Shops' },
	{ code: 'electronics_stores', name: 'Electronics Stores' },
	{ code: 'elementary_secondary_schools', name: 'Elementary, Secondary Schools' },
	{ code: 'emergency_services_gcas_visa_use_only', name: 'Emergency Services' },
	{ code: 'employment_temp_agencies', name: 'Employment/Temp Agencies' },
	{ code: 'equipment_rental', name: 'Equipment Rental' },
	{ code: 'exterminating_services', name: 'Exterminating Services' },
	{ code: 'family_clothing_stores', name: 'Family Clothing Stores' },
	{ code: 'fast_food_restaurants', name: 'Fast Food Restaurants' },
	{ code: 'financial_institutions', name: 'Financial Institutions' },
	{ code: 'fines_government_administrative_entities', name: 'Fines - Government' },
	{ code: 'fireplace_fireplace_screens_and_accessories_stores', name: 'Fireplace Stores' },
	{ code: 'floor_covering_stores', name: 'Floor Covering Stores' },
	{ code: 'florists', name: 'Florists' },
	{ code: 'florists_supplies_nursery_stock_and_flowers', name: 'Florists Supplies' },
	{ code: 'freezer_and_locker_meat_provisioners', name: 'Freezer and Locker Meat' },
	{ code: 'fuel_dealers_non_automotive', name: 'Fuel Dealers (Non Automotive)' },
	{ code: 'funeral_services_crematories', name: 'Funeral Services, Crematories' },
	{ code: 'furniture_repair_refinishing', name: 'Furniture Repair, Refinishing' },
	{ code: 'furniture_home_furnishings_and_equipment_stores_except_appliances', name: 'Furniture Stores' },
	{ code: 'furriers_and_fur_shops', name: 'Furriers and Fur Shops' },
	{ code: 'general_services', name: 'General Services' },
	{ code: 'gift_card_novelty_and_souvenir_shops', name: 'Gift, Card, Novelty Shops' },
	{ code: 'glass_paint_and_wallpaper_stores', name: 'Glass, Paint, Wallpaper Stores' },
	{ code: 'glassware_crystal_stores', name: 'Glassware, Crystal Stores' },
	{ code: 'golf_courses_public', name: 'Golf Courses - Public' },
	{ code: 'government_licensed_online_casions_online_gambling_us_region_only', name: 'Online Casinos' },
	{ code: 'government_services', name: 'Government Services' },
	{ code: 'government_licensed_horse_dog_racing_us_region_only', name: 'Horse/Dog Racing' },
	{ code: 'government_owned_lotteries_non_us_region', name: 'Lotteries (Non-US)' },
	{ code: 'government_owned_lotteries_us_region_only', name: 'Lotteries (US)' },
	{ code: 'grocery_stores_supermarkets', name: 'Grocery Stores, Supermarkets' },
	{ code: 'hardware_stores', name: 'Hardware Stores' },
	{ code: 'hardware_equipment_and_supplies', name: 'Hardware Equipment and Supplies' },
	{ code: 'health_and_beauty_spas', name: 'Health and Beauty Spas' },
	{ code: 'hearing_aids_sales_and_supplies', name: 'Hearing Aids Sales and Supplies' },
	{ code: 'heating_plumbing_a_c', name: 'Heating, Plumbing, A/C' },
	{ code: 'hobby_toy_and_game_shops', name: 'Hobby, Toy, and Game Shops' },
	{ code: 'home_supply_warehouse_stores', name: 'Home Supply Warehouse Stores' },
	{ code: 'hospitals', name: 'Hospitals' },
	{ code: 'hotels_motels_and_resorts', name: 'Hotels, Motels, and Resorts' },
	{ code: 'household_appliance_stores', name: 'Household Appliance Stores' },
	{ code: 'industrial_supplies', name: 'Industrial Supplies' },
	{ code: 'information_retrieval_services', name: 'Information Retrieval Services' },
	{ code: 'insurance_default', name: 'Insurance - Default' },
	{ code: 'insurance_underwriting_premiums', name: 'Insurance Underwriting' },
	{ code: 'intra_company_purchases', name: 'Intra-Company Purchases' },
	{ code: 'jewelry_stores_watches_clocks_and_silverware_stores', name: 'Jewelry Stores' },
	{ code: 'landscaping_services', name: 'Landscaping Services' },
	{ code: 'laundries', name: 'Laundries' },
	{ code: 'laundry_cleaning_services', name: 'Laundry, Cleaning Services' },
	{ code: 'legal_services_attorneys', name: 'Legal Services, Attorneys' },
	{ code: 'luggage_and_leather_goods_stores', name: 'Luggage and Leather Goods' },
	{ code: 'lumber_building_materials_stores', name: 'Lumber, Building Materials' },
	{ code: 'manual_cash_disburse', name: 'Manual Cash Disburse' },
	{ code: 'marinas_service_and_supplies', name: 'Marinas, Service and Supplies' },
	{ code: 'marketplaces', name: 'Marketplaces' },
	{ code: 'masonry_stonework_and_plaster', name: 'Masonry, Stonework' },
	{ code: 'massage_parlors', name: 'Massage Parlors' },
	{ code: 'medical_services', name: 'Medical Services' },
	{ code: 'medical_and_dental_labs', name: 'Medical and Dental Labs' },
	{ code: 'medical_dental_ophthalmic_and_hospital_equipment_and_supplies', name: 'Medical Equipment' },
	{ code: 'membership_organizations', name: 'Membership Organizations' },
	{ code: 'mens_and_boys_clothing_and_accessories_stores', name: 'Mens and Boys Clothing' },
	{ code: 'mens_womens_clothing_stores', name: 'Mens, Womens Clothing' },
	{ code: 'metal_service_centers', name: 'Metal Service Centers' },
	{ code: 'miscellaneous_apparel_and_accessory_shops', name: 'Misc Apparel Shops' },
	{ code: 'miscellaneous_auto_dealers', name: 'Misc Auto Dealers' },
	{ code: 'miscellaneous_business_services', name: 'Misc Business Services' },
	{ code: 'miscellaneous_food_stores', name: 'Misc Food Stores' },
	{ code: 'miscellaneous_general_merchandise', name: 'Misc General Merchandise' },
	{ code: 'miscellaneous_general_services', name: 'Misc General Services' },
	{ code: 'miscellaneous_home_furnishing_specialty_stores', name: 'Misc Home Furnishing' },
	{ code: 'miscellaneous_publishing_and_printing', name: 'Misc Publishing' },
	{ code: 'miscellaneous_recreation_services', name: 'Misc Recreation Services' },
	{ code: 'miscellaneous_repair_shops', name: 'Misc Repair Shops' },
	{ code: 'miscellaneous_specialty_retail', name: 'Misc Specialty Retail' },
	{ code: 'mobile_home_dealers', name: 'Mobile Home Dealers' },
	{ code: 'motion_picture_theaters', name: 'Motion Picture Theaters' },
	{ code: 'motor_freight_carriers_and_trucking', name: 'Motor Freight Carriers' },
	{ code: 'motor_homes_dealers', name: 'Motor Homes Dealers' },
	{ code: 'motor_vehicle_supplies_and_new_parts', name: 'Motor Vehicle Supplies' },
	{ code: 'motorcycle_shops_and_dealers', name: 'Motorcycle Shops and Dealers' },
	{ code: 'motorcycle_shops_dealers', name: 'Motorcycle Shops' },
	{ code: 'music_stores_musical_instruments_pianos_and_sheet_music', name: 'Music Stores' },
	{ code: 'news_dealers_and_newsstands', name: 'News Dealers and Newsstands' },
	{ code: 'non_fi_money_orders', name: 'Non-FI, Money Orders' },
	{ code: 'non_fi_stored_value_card_purchase_load', name: 'Stored Value Card Purchase' },
	{ code: 'nondurable_goods', name: 'Nondurable Goods' },
	{ code: 'nurseries_lawn_and_garden_supply_stores', name: 'Nurseries, Garden Supply' },
	{ code: 'nursing_personal_care', name: 'Nursing/Personal Care' },
	{ code: 'office_and_commercial_furniture', name: 'Office and Commercial Furniture' },
	{ code: 'opticians_eyeglasses', name: 'Opticians, Eyeglasses' },
	{ code: 'optometrists_ophthalmologist', name: 'Optometrists' },
	{ code: 'orthopedic_goods_prosthetic_devices', name: 'Orthopedic Goods' },
	{ code: 'osteopaths', name: 'Osteopaths' },
	{ code: 'package_stores_beer_wine_and_liquor', name: 'Package Stores - Liquor' },
	{ code: 'paints_varnishes_and_supplies', name: 'Paints, Varnishes' },
	{ code: 'parking_lots_garages', name: 'Parking Lots, Garages' },
	{ code: 'passenger_railways', name: 'Passenger Railways' },
	{ code: 'pawn_shops', name: 'Pawn Shops' },
	{ code: 'pet_shops_pet_food_and_supplies', name: 'Pet Shops, Pet Food' },
	{ code: 'petroleum_and_petroleum_products', name: 'Petroleum Products' },
	{ code: 'photo_developing', name: 'Photo Developing' },
	{ code: 'photographic_studios', name: 'Photographic Studios' },
	{ code: 'photographic_photocopy_microfilm_equipment_and_supplies', name: 'Photographic Equipment' },
	{ code: 'picture_video_production', name: 'Picture/Video Production' },
	{ code: 'piece_goods_notions_and_other_dry_goods', name: 'Piece Goods, Notions' },
	{ code: 'plumbing_heating_equipment_and_supplies', name: 'Plumbing Equipment' },
	{ code: 'political_organizations', name: 'Political Organizations' },
	{ code: 'postal_services_government_only', name: 'Postal Services' },
	{ code: 'precious_stones_and_metals_watches_and_jewelry', name: 'Precious Stones and Metals' },
	{ code: 'professional_services', name: 'Professional Services' },
	{ code: 'public_warehousing_and_storage', name: 'Public Warehousing' },
	{ code: 'quick_copy_repro_and_blueprint', name: 'Quick Copy, Blueprint' },
	{ code: 'railroads', name: 'Railroads' },
	{ code: 'real_estate_agents_and_managers_rentals', name: 'Real Estate Agents' },
	{ code: 'record_stores', name: 'Record Stores' },
	{ code: 'recreational_vehicle_rentals', name: 'Recreational Vehicle Rentals' },
	{ code: 'religious_goods_stores', name: 'Religious Goods Stores' },
	{ code: 'religious_organizations', name: 'Religious Organizations' },
	{ code: 'roofing_siding_sheet_metal', name: 'Roofing/Siding' },
	{ code: 'secretarial_support_services', name: 'Secretarial Support' },
	{ code: 'security_brokers_dealers', name: 'Security Brokers/Dealers' },
	{ code: 'service_stations', name: 'Service Stations' },
	{ code: 'sewing_needlework_fabric_and_piece_goods_stores', name: 'Sewing, Fabric Stores' },
	{ code: 'shoe_repair_hat_cleaning', name: 'Shoe Repair' },
	{ code: 'shoe_stores', name: 'Shoe Stores' },
	{ code: 'small_appliance_repair', name: 'Small Appliance Repair' },
	{ code: 'snowmobile_dealers', name: 'Snowmobile Dealers' },
	{ code: 'special_trade_services', name: 'Special Trade Services' },
	{ code: 'specialty_cleaning', name: 'Specialty Cleaning' },
	{ code: 'sporting_goods_stores', name: 'Sporting Goods Stores' },
	{ code: 'sporting_recreation_camps', name: 'Sporting/Recreation Camps' },
	{ code: 'sports_clubs_fields', name: 'Sports Clubs/Fields' },
	{ code: 'sports_and_riding_apparel_stores', name: 'Sports Apparel Stores' },
	{ code: 'stamp_and_coin_stores', name: 'Stamp and Coin Stores' },
	{ code: 'stationery_stores_office_and_school_supply_stores', name: 'Stationery Stores' },
	{ code: 'stationary_office_supplies_printing_and_writing_paper', name: 'Office Supplies' },
	{ code: 'swimming_pools_sales', name: 'Swimming Pools Sales' },
	{ code: 't_ui_travel_germany', name: 'TUI Travel - Germany' },
	{ code: 'tailors_alterations', name: 'Tailors, Alterations' },
	{ code: 'tax_payments_government_agencies', name: 'Tax Payments' },
	{ code: 'tax_preparation_services', name: 'Tax Preparation Services' },
	{ code: 'taxicabs_limousines', name: 'Taxicabs/Limousines' },
	{ code: 'telecommunication_equipment_and_telephone_sales', name: 'Telecom Equipment' },
	{ code: 'telecommunication_services', name: 'Telecommunication Services' },
	{ code: 'telegraph_services', name: 'Telegraph Services' },
	{ code: 'tent_and_awning_shops', name: 'Tent and Awning Shops' },
	{ code: 'testing_laboratories', name: 'Testing Laboratories' },
	{ code: 'theatrical_ticket_agencies', name: 'Theatrical Ticket Agencies' },
	{ code: 'timeshares', name: 'Timeshares' },
	{ code: 'tire_retreading_and_repair', name: 'Tire Retreading and Repair' },
	{ code: 'tolls_bridge_fees', name: 'Tolls/Bridge Fees' },
	{ code: 'tourist_attractions_and_exhibits', name: 'Tourist Attractions' },
	{ code: 'towing_services', name: 'Towing Services' },
	{ code: 'trailer_parks_campgrounds', name: 'Trailer Parks, Campgrounds' },
	{ code: 'transportation_services', name: 'Transportation Services' },
	{ code: 'travel_agencies_tour_operators', name: 'Travel Agencies' },
	{ code: 'truck_utility_trailer_rentals', name: 'Truck/Trailer Rentals' },
	{ code: 'typesetting_plate_making_and_related_services', name: 'Typesetting Services' },
	{ code: 'typewriter_stores', name: 'Typewriter Stores' },
	{ code: 'u_s_federal_government_agencies_or_departments', name: 'U.S. Federal Government' },
	{ code: 'uniforms_commercial_clothing', name: 'Uniforms, Commercial Clothing' },
	{ code: 'used_merchandise_and_secondhand_stores', name: 'Used Merchandise Stores' },
	{ code: 'utilities', name: 'Utilities' },
	{ code: 'variety_stores', name: 'Variety Stores' },
	{ code: 'veterinary_services', name: 'Veterinary Services' },
	{ code: 'video_amusement_game_supplies', name: 'Video Game Supplies' },
	{ code: 'video_game_arcades', name: 'Video Game Arcades' },
	{ code: 'video_tape_rental_stores', name: 'Video Tape Rental Stores' },
	{ code: 'vocational_trade_schools', name: 'Vocational/Trade Schools' },
	{ code: 'watch_jewelry_repair', name: 'Watch/Jewelry Repair' },
	{ code: 'welding_repair', name: 'Welding Repair' },
	{ code: 'wholesale_clubs', name: 'Wholesale Clubs' },
	{ code: 'wig_and_toupee_stores', name: 'Wig and Toupee Stores' },
	{ code: 'wires_money_orders', name: 'Wires, Money Orders' },
	{ code: 'womens_accessory_and_specialty_shops', name: 'Womens Accessory Shops' },
	{ code: 'womens_ready_to_wear_stores', name: 'Womens Ready-To-Wear' },
	{ code: 'wrecking_and_salvage_yards', name: 'Wrecking and Salvage Yards' },
];

export default defineComponent({
	name: "MerchantCategoriesBottomSheet",
	components: { ModalHeader, AppList, AppTextInput },
	emits: ["apply"],
	props: {
		selectedCategories: {
			type: Array,
			default: () => []
		},
		mode: {
			type: String,
			default: 'allowed', // 'allowed' or 'blocked'
			validator: (value) => ['allowed', 'blocked'].includes(value)
		}
	},
	setup() {
		const languageStore = useLanguageStore();
		return { 
			getString: languageStore.getString 
		};
	},
	data() {
		return {
			searchQuery: '',
			modSelectedCategories: []
		}
	},
	computed: {
		filteredCategories() {
			const query = this.searchQuery.toLowerCase();
			const categories = MERCHANT_CATEGORIES.filter(cat => 
				cat.name.toLowerCase().includes(query) || 
				cat.code.toLowerCase().includes(query)
			);
			
			return categories.map(cat => ({
				title: cat.name,
				switch: true,
				active: this.modSelectedCategories.includes(cat.code),
				callback: () => this.toggleCategory(cat.code)
			}));
		}
	},
	methods: {
		toggleCategory(code) {
			const index = this.modSelectedCategories.indexOf(code);
			if (index > -1) {
				this.modSelectedCategories.splice(index, 1);
			} else {
				this.modSelectedCategories.push(code);
			}
		},
		apply() {
			this.$emit('apply', this.modSelectedCategories);
		}
	},
	mounted() {
		this.modSelectedCategories = [...this.selectedCategories];
	},
	watch: {
		selectedCategories: {
			handler(newCategories) {
				this.modSelectedCategories = [...newCategories];
			},
			immediate: true
		}
	}
});
</script>

<style scoped lang="scss">
.merchant-categories-container {
	display: flex;
	flex-direction: column;
	height: 100%;
	
	.categories-search {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding: 10px 0;
	}
	
	.categories-list {
		flex: 1;
		overflow-y: auto;
		padding-bottom: 20px;
	}
}
</style>